"use client";

import Link from "@/infrastructure/router/Link";
import { useEffect, useRef } from "react";
import { ArtistIcon } from "@/components/design-system/ArtistDesignSystem";
import { useTranslation } from "@/i18n/ArtistI18n";

/**
 * CreateDialog — Web port of iOS `MakeMenu` (ContentView.swift).
 *
 * iOS presents three options as a bottom sheet (`.presentationDetents([.height(250)])`):
 *   1. 创作歌曲 (composeSong) → opens MakePage (full screen cover)
 *   2. 拍照 (camera) → opens ImagePicker (camera)
 *   3. 写笔记 (writeNote) → opens WriteNotePage
 *
 * On Web, the same three options render in a centered `<dialog>` with the
 * iOS-faithful icon + title rows. Selecting an option routes to the closest
 * Web equivalent (`/make`, `/canvas` for camera upload, `/note/compose`).
 */
export function CreateDialog({
	open,
	onClose,
}: Readonly<{ open: boolean; onClose: () => void }>) {
	const dialog = useRef<HTMLDialogElement>(null);
	const returnFocus = useRef<HTMLElement>(null);
	const { t } = useTranslation();

	useEffect(() => {
		const element = dialog.current;
		if (element === null) return;
		if (open) {
			returnFocus.current =
				document.activeElement instanceof HTMLElement
					? document.activeElement
					: null;
			if (typeof element.showModal === "function") {
				if (!element.open) element.showModal();
			} else {
				element.setAttribute("open", "");
			}
			element.querySelector<HTMLButtonElement>(".dialog-close")?.focus();
			return;
		}
		if (element.open) {
			if (typeof element.close === "function") element.close();
			else element.removeAttribute("open");
		}
		returnFocus.current?.focus();
		returnFocus.current = null;
	}, [open]);

	return (
		<dialog
			ref={dialog}
			className="dialog-backdrop create-dialog-root"
			aria-labelledby="create-title"
			onCancel={(event) => {
				event.preventDefault();
				onClose();
			}}
			onClick={(event) => {
				if (event.target === event.currentTarget) onClose();
			}}
			onKeyDown={(event) => {
				if (event.key === "Escape") {
					event.preventDefault();
					onClose();
				}
			}}
		>
			<section className="create-dialog">
				<button
					className="dialog-close"
					type="button"
					onClick={onClose}
					aria-label={t("make.close")}
				>
					×
				</button>
				<p className="eyebrow">{t("make.eyebrow")}</p>
				<h2 id="create-title">{t("make.title")}</h2>
				<div className="create-options">
					<Link href="/make" onClick={onClose} className="create-option">
						<span className="create-option__icon" aria-hidden="true">
							<ArtistIcon name="sparkle" size={22} />
						</span>
						<span className="create-option__copy">
							<strong>{t("make.composeSong")}</strong>
							<span>{t("make.composeSong.subtitle")}</span>
						</span>
					</Link>
					<Link href="/canvas" onClick={onClose} className="create-option">
						<span className="create-option__icon" aria-hidden="true">
							<ArtistIcon name="camera" size={22} />
						</span>
						<span className="create-option__copy">
							<strong>{t("make.camera")}</strong>
							<span>{t("make.camera.subtitle")}</span>
						</span>
					</Link>
					<Link
						href="/note/compose"
						onClick={onClose}
						className="create-option"
					>
						<span className="create-option__icon" aria-hidden="true">
							<ArtistIcon name="message" size={22} />
						</span>
						<span className="create-option__copy">
							<strong>{t("make.writeNote")}</strong>
							<span>{t("make.writeNote.subtitle")}</span>
						</span>
					</Link>
				</div>
				<button type="button" className="create-cancel" onClick={onClose}>
					{t("make.cancel")}
				</button>
			</section>
		</dialog>
	);
}
